import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/rootReducer";
import styles from "../Assessment.module.scss";
import { fetchCandidatesAPI, inviteCandidatesAPI, fetchAssessmentByIdAPI } from "@/services/api/assessment";
import toast from "react-hot-toast";
import { formatDate, copyToClipboard, combineClasses } from "@/lib/utils";
import Table from "@/components/Table/Table";
import Pagination from "@/components/Table/Pagination";
import SearchBox from "@/components/SearchBox/SearchBox";
import CandidateFilterModal from "./CandidateFilterModal";
import { ExportIcon } from "@/assets/svg/Other/ExportIcon";
import FilterIcon from "@/components/SVGComponents/FilterIcon";

interface CandidateData {
  name?: string;
  email?: string;
  status?: string;
  joinedAt?: string;
  timeTaken?: string;
  score?: string | number;
  cheating?: boolean;
}

interface AssessmentData {
  id: number;
  assessmentId?: number;
  name: string;
  email?: string;
  challengeCount: number;
  questionCount: number;
  freeformCount: number;
  invitedCount: number;
  assessedCount: number;
  qualifiedCount: number;
  qualifyingPercentage?: number;
  createdByName: string;
  createdAt: string;
  lastActivity?: string;
  isPublic?: boolean;
  publicToken?: string;
}

interface AssessmentDetailProps {
  assessment: AssessmentData;
  onBack: () => void;
  onEdit?: () => void;
}

const AssessmentDetail: React.FC<AssessmentDetailProps> = ({ assessment: initialAssessment, onBack, onEdit }) => {
  const navigate = useNavigate();
  // Resolve ID because backend uses assessmentId while frontend sometimes uses id
  const assessmentId = initialAssessment.assessmentId || (initialAssessment as any).id;

  const { profile } = useSelector((state: RootState) => state.user);
  const currentUserRole = profile?.role?.toUpperCase();
  const isOwnerOrManager = currentUserRole === "OWNER" || currentUserRole === "MANAGER" || currentUserRole === "ADMIN";

  const [assessment, setAssessment] = useState<AssessmentData>(initialAssessment);
  const [candidates, setCandidates] = useState<CandidateData[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Pagination State
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 10;

  // Invite Section State
  const [emailInput, setEmailInput] = useState("");
  const [isInviting, setIsInviting] = useState(false);

  // Filter States
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [filterCheatingStatus, setFilterCheatingStatus] = useState<string>("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const filterStatusRef = useRef(filterStatus);
  const filterCheatingStatusRef = useRef(filterCheatingStatus);

  // Fetch full assessment details (with actual counts) since list view returns 0s
  const loadAssessmentDetails = useCallback(async () => {
    if (!assessmentId) return;
    setLoadingDetail(true);
    try {
      const res = await fetchAssessmentByIdAPI(assessmentId);
      if (res?.data) {
        setAssessment(res.data);
      } else if (res) {
        setAssessment(res);
      }
    } catch (err) {
      console.error("Failed to fetch assessment details", err);
    } finally {
      setLoadingDetail(false);
    }
  }, [assessmentId]);

  const loadCandidates = useCallback(async (
    searchTerm: string = "",
    targetPage: number = 1,
    statusFilter?: string,
    cheatingFilter?: string
  ) => {
    if (!assessmentId) return;
    setLoadingCandidates(true);
    try {
      const activeStatus = statusFilter !== undefined ? statusFilter : filterStatusRef.current;
      const activeCheating = cheatingFilter !== undefined ? cheatingFilter : filterCheatingStatusRef.current;

      const res = await fetchCandidatesAPI(
        assessmentId,
        false,
        searchTerm,
        targetPage,
        pageSize,
        activeStatus || undefined,
        activeCheating || undefined
      );

      // Robust extraction for spring GenericResponse + PageDto structure
      let list: CandidateData[] = [];
      const resData = res?.data || res;

      if (resData?.results && Array.isArray(resData.results)) {
        list = resData.results;
        setTotalRecords(resData.totalRecord || 0);
      } else if (Array.isArray(resData)) {
        list = resData;
        setTotalRecords(resData.length);
      }

      setCandidates(list);
    } catch (err) {
      console.error("Failed to fetch candidates", err);
    } finally {
      setLoadingCandidates(false);
    }
  }, [assessmentId]);

  // Sync refs when states change
  useEffect(() => {
    filterStatusRef.current = filterStatus;
    filterCheatingStatusRef.current = filterCheatingStatus;
  }, [filterStatus, filterCheatingStatus]);

  const handleApplyFilters = (newStatus: string, newCheating: string) => {
    setFilterStatus(newStatus);
    setFilterCheatingStatus(newCheating);
    filterStatusRef.current = newStatus;
    filterCheatingStatusRef.current = newCheating;

    if (page === 1) {
      loadCandidates(searchQuery, 1, newStatus, newCheating);
    } else {
      setPage(1);
    }
    setIsFilterModalOpen(false);
  };

  const handleResetFilters = () => {
    setFilterStatus("");
    setFilterCheatingStatus("");
    filterStatusRef.current = "";
    filterCheatingStatusRef.current = "";

    if (page === 1) {
      loadCandidates(searchQuery, 1, "", "");
    } else {
      setPage(1);
    }
    setIsFilterModalOpen(false);
  };

  // Parent Assessment.tsx already fetches the full assessment by ID on mount.
  // We don't need to re-fetch on mount here.
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1); // Reset to first page on search
    }, 400);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load candidates when page or searchQuery changes
  useEffect(() => {
    if (isOwnerOrManager) {
      loadCandidates(searchQuery, page);
    }
  }, [page, searchQuery, loadCandidates, isOwnerOrManager]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleExport = async () => {
    try {
      const blob = await fetchCandidatesAPI(
        assessmentId,
        true,
        searchQuery,
        1,
        10000,
        filterStatus || undefined,
        filterCheatingStatus || undefined
      );
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${assessment.name || 'Assessment'}_Candidates.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to export candidates", err);
    }
  };

  const handleInvite = async () => {
    if (!emailInput.trim()) return;
    const emails = emailInput.split(',').map(e => e.trim()).filter(e => e);
    if (emails.length === 0) return;

    setIsInviting(true);
    try {
      await inviteCandidatesAPI(assessmentId, emails);
      toast.success("Invitations sent successfully");
      setEmailInput("");
      loadCandidates();
    } catch (err) {
      toast.error("Failed to send invitations");
    } finally {
      setIsInviting(false);
    }
  };

  const filteredCandidates = candidates;

  const hasActiveFilters = !!filterStatus || !!filterCheatingStatus;
  const activeFiltersCount = (filterStatus ? 1 : 0) + (filterCheatingStatus ? 1 : 0);

  const invited = assessment.invitedCount || (assessment as any).invited_count || (assessment as any).invited || 0;
  const assessed = assessment.assessedCount || (assessment as any).assessed_count || (assessment as any).assessed || 0;
  const qualified = assessment.qualifiedCount || (assessment as any).qualified_count || (assessment as any).qualified || 0;

  const getCount = (val: any) => {
    if (Array.isArray(val)) return val.length;
    return typeof val === 'number' ? val : 0;
  };

  const challenges = assessment.challengeCount ?? getCount((assessment as any).challenges);
  const questions = assessment.questionCount ?? getCount((assessment as any).questions);
  const freeform = assessment.freeformCount ?? getCount((assessment as any).free_forms || (assessment as any).freeform || (assessment as any).freeForm);

  // Use backend calculated percentage if available, otherwise calculate locally
  const qualifyingRate = (assessment as any).qualifyingPercentage !== undefined
    ? Math.round((assessment as any).qualifyingPercentage)
    : (invited > 0 ? Math.round((qualified / invited) * 100) : 0);

  const getStatusClass = (status: string) => {
    const s = status?.toLowerCase() || "";
    if (s.includes("submitted")) return styles.statusSubmitted;
    if (s.includes("in progress")) return styles.statusInProgress;
    if (s.includes("invited")) return styles.statusInvited;
    if (s.includes("expired")) return styles.statusTimeExpired;
    return styles.statusInvited;
  };

  const getCheatingClass = (cheating: string) => {
    const c = cheating?.toLowerCase() || "";
    if (c.includes("not detected") || !c) return styles.cheatNone;
    if (c.includes("likely")) return styles.cheatLikely;
    if (c.includes("detect") || c.includes("cheat")) return styles.cheatDetected;
    return "";
  };

  const columns = [
    {
      name: "Name",
      selector: (row: any) => row.name || "N/A",
      sortable: true,
      width: "150px",
    },
    {
      name: "Email",
      selector: (row: any) => row.email,
      sortable: true,
      width: "260px",
    },
    {
      name: "Status",
      cell: (row: any) => (
        <span className={`${styles.statusBadge} ${getStatusClass(row.status)}`}>
          {row.status || "Invited"}
        </span>
      ),
      sortable: true,
      width: "150px",
    },
    {
      name: "Joined",
      selector: (row: any) => row.joined || "N/A",
      sortable: true,
      width: "140px",
    },
    {
      name: "Time taken",
      selector: (row: any) => row.timeTaken || "N/A",
      sortable: true,
      width: "140px",
    },
    {
      name: "Score",
      cell: (row: any) => (
        <span className={styles.scoreCell}>{row.finalScore || "0%"}</span>
      ),
      sortable: true,
      width: "130px",
    },
    {
      name: "Cheating",
      cell: (row: any) => (
        <span className={getCheatingClass(row.cheating)}>
          {(() => {
            const lower = row.cheating?.toLowerCase() || "";
            if (lower.includes("not detected") || !lower) return "Not detected";
            if (lower.includes("likely")) return "Likely";
            if (lower.includes("cheat") || lower.includes("detect")) return "Detected";
            return row.cheating;
          })()}
        </span>
      ),
      sortable: true,
      width: "170px",
    },
    {
      name: "Actions",
      cell: (row: any) => (
        <div className={styles.refActions}>
          <button
            onClick={() => {
              const cid = row.candidateId || row.id || (row.row && row.row.candidateId);
              if (!cid) {
                console.error("Missing candidate ID for row:", row);
                toast.error("Candidate ID is missing");
                return;
              }
              navigate(`/report/${assessmentId}/${cid}`);
            }}
            className={styles.reportBtn}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
            View report
          </button>
          {/* <button className={styles.moreBtn}>⋮</button> */}
        </div>
      ),
      width: "150px",
    }
  ];

  return (
    <div className={styles.imageDetailContainer}>
      {/* Back Link */}
      <button className={styles.imageBackLink} onClick={onBack}>
        &lt; Back to all assessments
      </button>

      {/* Header */}
      <div className={styles.imageHeader}>
        <div className={styles.headerTitleArea}>
          <h1 className={styles.imageTitle}>{assessment.name}</h1>
          <p className={styles.imageSubtitle}>
            Managed by <strong>{assessment.email}</strong> • Created on{" "}
            {formatDate(assessment.createdAt)}
          </p>
        </div>
        <div className={styles.headerActions}>
          <div className={styles.previewGroup}>
            <button className={styles.previewBtn} onClick={() => {
              localStorage.removeItem(`assessment_end_time_preview_${assessmentId}`);
              localStorage.removeItem(`assessment_duration_preview_${assessmentId}`);
              navigate(`/assessment-preview/${assessmentId}`);
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3" /></svg>
              Preview
            </button>
            {/* <div className={styles.toggleSwitch}>
              <input type="checkbox" id="previewToggle" defaultChecked />
              <label htmlFor="previewToggle"></label>
            </div> */}
          </div>
          <button className={styles.imageEditBtn} onClick={onEdit}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
            Edit
          </button>
        </div>
      </div>

      {/* Top Cards Grid */}
      <div className={styles.imageCardsGrid}>
        {/* Card 1: Invite */}

        <div className={styles.imageCard}>
          <h3 className={styles.imageCardTitle}>Invite candidates</h3>
          <input
            className={styles.imageInviteInput}
            placeholder="john@email.com,abc@gmail.com..."
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
          />
          <div className={styles.imageCardButtons}>
            <button
              className={`${styles.sendInviteBtn} ${!isOwnerOrManager ? styles.disabledBtn : ""
                }`}
              disabled={isInviting}
              onClick={() => {
                if (!isOwnerOrManager) {
                  toast.error("You don't have permission.");
                  return;
                }

                handleInvite();
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ marginRight: "6px" }}
              >
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
              {isInviting ? "Sending..." : "Send private link"}
            </button>
            <span className={styles.orText}>OR</span>
            <button
              className={`${styles.copyLinkBtn} ${!isOwnerOrManager ? styles.disabledBtn : ""
                }`}
              onClick={() => {
                if (!isOwnerOrManager) {
                  toast.error("You don't have permission.");
                  return;
                }

                if (!assessment.isPublic) {
                  toast.error(
                    "This assessment is not public. Please edit it to enable public access first."
                  );
                  return;
                }

                const publicLink = `${window.location.origin}/assessment/invitation/begin/${assessment.publicToken}`;

                copyToClipboard(publicLink).then((success) => {
                  if (success) {
                    toast.success("Public link copied to clipboard!");
                  } else {
                    toast.error("Failed to copy link. Please copy it manually.");
                  }
                });
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ marginRight: "6px" }}
              >
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              Copy public link
            </button>
          </div>
        </div>


        {/* Card 2: Assessment Details */}
        <div className={styles.imageCard}>
          <h3 className={styles.imageCardTitle}>Assessment details</h3>
          <div className={styles.assessmentStatsRow}>
            <div className={styles.statItemBig}>
              <span className={styles.statNumberLarge}>{challenges}</span>
              <span className={styles.statLabelSmall}>Challenges</span>
            </div>
            <div className={styles.statItemBig}>
              <span className={styles.statNumberLarge}>{questions}</span>
              <span className={styles.statLabelSmall}>Questions</span>
            </div>
            <div className={styles.statItemBig}>
              <span className={styles.statNumberLarge}>{freeform}</span>
              <span className={styles.statLabelSmall}>Free-form</span>
            </div>
          </div>
        </div>

        {/* Card 3: Candidate Details */}
        <div className={styles.imageCard}>
          <h3 className={styles.imageCardTitle}>Candidate details</h3>
          <div className={styles.statsSummaryGrid}>
            <div className={styles.mainStat}>
              <span className={styles.mainStatValue}>{qualifyingRate}%</span>
              <span className={styles.mainStatLabel}>Qualifying</span>
            </div>
            <div className={styles.divider} />
            <div className={styles.sideStats}>
              <div className={styles.sideStatItem}>
                <span className={styles.sideStatValue}>{invited}</span>
                <span className={styles.sideStatLabel}>invited</span>
              </div>
              <div className={styles.sideStatItem}>
                <span className={styles.sideStatValue}>{assessed}</span>
                <span className={styles.sideStatLabel}>assessed</span>
              </div>
              <div className={styles.sideStatItem}>
                <span className={styles.sideStatValue}>{qualified}</span>
                <span className={styles.sideStatLabel}>qualified</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Table Section */}
      {isOwnerOrManager && (
        <div className={styles.imageTableSection}>
          <div className={styles.tableToolbar} style={{ justifyContent: 'flex-end' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <SearchBox
                value={searchQuery}
                onChange={(val) => setSearchQuery(val)}
                // placeholder="Search for candidates..."
                width={240}
              />
              <button
                className={combineClasses(styles.filterBtn, hasActiveFilters && styles.activeFilters)}
                onClick={() => setIsFilterModalOpen(true)}
              >
                <FilterIcon />
                {activeFiltersCount > 0 && (
                  <span className={styles.filterBadge}>{activeFiltersCount}</span>
                )}
              </button>
              {hasActiveFilters && (
                <button
                  className={styles.clearFiltersBtn}
                  onClick={handleResetFilters}
                >
                  Clear
                </button>
              )}
              <button
                className={styles.exportBtn}
                onClick={handleExport}
              >
                <ExportIcon /> Export
              </button>
            </div>
          </div>

          <div className={styles.imageTableWrapper}>
            <Table
              columns={columns}
              data={Array.isArray(filteredCandidates) ? filteredCandidates : []}
              progressPending={loadingCandidates}
            />
          </div>

          <Pagination
            totalCount={totalRecords}
            pageSelected={page}
            setPageSelected={setPage}
            rowsPerPageValue={pageSize}
          />
        </div>
      )}

      <CandidateFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        status={filterStatus}
        cheatingStatus={filterCheatingStatus}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />
    </div>
  );
};

export default AssessmentDetail;
